import React, { useMemo, useEffect, useState, useRef, useLayoutEffect } from 'react';
import globalAudio from '../lib/globalAudio.js';
import { formatTime } from '../utils/helpers.js';

const MediaPlayerBase = ({
    fileType,
    mediaSrc,
    videoPlayerRef,
    audioPlayerRef,
    isPlaying,
    togglePlayPause,
    handleSeek,
    currentTime,
    mediaDuration,
    syncMediaState
}) => {
    const containerRef = useRef(null);
    
    // Attach global audio element and set src without remounting
    useLayoutEffect(() => {
        if (fileType !== 'audio') return;
        const el = globalAudio.get();
        try { el.controls = false; } catch (_) {}
        if (audioPlayerRef) audioPlayerRef.current = el;
        if (containerRef.current) globalAudio.attach(containerRef.current);
        if (mediaSrc) globalAudio.setSource(mediaSrc);
        // do not clear on dependency changes to preserve playback; only on unmount when no mediaSrc provided
        return () => {};
    }, [fileType, mediaSrc, audioPlayerRef]);

    const progressPct = Number.isFinite(currentTime) && Number.isFinite(mediaDuration) && mediaDuration > 0
        ? Math.max(0, Math.min(100, (currentTime / mediaDuration) * 100))
        : 0;
    return (
        <div className="rounded-2xl border border-white/10 bg-slate-900/60 overflow-hidden">
            {fileType === 'video' ? (
                <>
                    <div className="relative">
                        <video
                            ref={videoPlayerRef}
                            src={mediaSrc}
                            playsInline
                            className="w-full max-h-[360px] object-contain bg-black"
                            onClick={togglePlayPause}
                            onLoadedMetadata={syncMediaState}
                            onLoadedData={syncMediaState}
                            onCanPlayThrough={syncMediaState}
                        >
                        </video>
                    </div>
                    <div className="flex items-center gap-3 px-4 py-3 border-t border-white/10">
                        <button onClick={togglePlayPause} className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-slate-800/70 hover:bg-slate-800 text-slate-200 ring-1 ring-white/10">
                            {isPlaying ? '❚❚' : '▶'}
                        </button>
                        <div className="relative flex-1 h-2 rounded-full bg-slate-800/80 cursor-pointer" onClick={handleSeek}>
                            <div className="absolute inset-y-0 left-0 h-2 rounded-full bg-gradient-to-r from-teal-600 to-emerald-600" style={{ width: `${progressPct}%` }}></div>
                        </div>
                        <span className="text-xs tabular-nums text-slate-300 min-w-[120px] text-right">{formatTime(currentTime)} / {formatTime(mediaDuration)}</span>
                    </div>
                </>
            ) : (
                <>
                    {/* Hidden attached audio element (global) */}
                    <div className="px-4 pt-3">
                        <div ref={containerRef} style={{ display: 'none' }} />
                    </div>
                    {/* Custom controls (same style as video) */}
                    <div className="flex items-center gap-3 px-4 py-3 border-t border-white/10">
                        <button aria-label={isPlaying ? 'Pause' : 'Play'} onClick={togglePlayPause} className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-slate-800/70 hover:bg-slate-800 text-slate-200 ring-1 ring-white/10">
                            {isPlaying ? '❚❚' : '▶'}
                        </button>
                        <div className="relative flex-1 h-2 rounded-full bg-slate-800/80 cursor-pointer" onClick={handleSeek}>
                            <div className="absolute inset-y-0 left-0 h-2 rounded-full bg-gradient-to-r from-teal-600 to-emerald-600" style={{ width: `${progressPct}%` }}></div>
                        </div>
                        <span className="text-xs tabular-nums text-slate-300 min-w-[120px] text-right">{formatTime(currentTime)} / {formatTime(mediaDuration)}</span>
                    </div>
                </>
            )}
        </div>
    );
};

const MediaPlayer = React.memo(MediaPlayerBase, (prev, next) => (
    prev.fileType === next.fileType &&
    prev.mediaSrc === next.mediaSrc &&
    prev.isPlaying === next.isPlaying &&
    prev.currentTime === next.currentTime &&
    prev.mediaDuration === next.mediaDuration
));

export default MediaPlayer;


